import { answered } from '@/lib/reportsRead';
import { useBankAccounts } from '@/hooks/queries/useBankIngestion';
import { usePaymentProcessors } from '@/hooks/queries/usePaymentProcessorRate';
import { useFinanceSettings } from '@/hooks/queries/useFinanceSettings';
import { useCurrentClosePeriod } from '@/hooks/queries/useCloseHub';

/**
 * CPH-012 onboarding precedence: drives the Close hub's pre-spine flow.
 *
 * USI-005 (ADR-011) collapsed the three-phase tracker; the
 * `spine:waiting-for-activity` state retired with `usePhaseState` (no
 * `phase.sort.tied` to delegate to). The remaining four states keep their
 * meaning:
 *
 * - `loading` — the gate-deciding queries are still in flight on a cold load;
 *   we don't yet know if this is a fresh campaign or an established one, so the
 *   layout holds a neutral skeleton instead of guessing a gate.
 * - `gate:no-sources` — link a bank or processor first.
 * - `gate:no-formation-date` — set the committee formation date first.
 * - `no-period` — no open `FilingDeadline` resolves; show the empty state.
 * - `spine:active` — render the inbox (post-USI-007) inside the layout.
 * - `settings-failed` / `settings-offline` — the finance settings read could not answer, so neither
 *   gate can be told apart from real history; the layout names the read instead of guessing a gate.
 */
export type CloseHubOnboardingState =
  | 'loading'
  | 'gate:no-sources'
  | 'gate:no-formation-date'
  | 'no-period'
  | 'spine:active'
  | 'settings-failed'
  | 'settings-offline'
  | 'bank-accounts-failed'
  | 'bank-accounts-offline'
  | 'processors-failed'
  | 'processors-offline';

export function useCloseHubOnboardingState(): CloseHubOnboardingState {
  const bankAccounts = useBankAccounts();
  const processors = usePaymentProcessors();
  const settings = useFinanceSettings();
  const period = useCurrentClosePeriod();

  // Loading-state policy (coordinated with the `CloseLayout` consumer): while
  // ANY gate-deciding query is still in flight on a cold load, we cannot tell a
  // freshly-onboarded campaign (genuinely no sources) from an established one
  // whose data simply hasn't arrived. Reporting a gate here flashed "Connect a
  // source to start." over real history and read as data loss. Report `loading`
  // instead and let the layout hold a neutral skeleton until the truth arrives.
  // TanStack v5 `isLoading` is `isPending && isFetching`, so it is true only on
  // the FIRST fetch with no cache — background refetches won't re-trigger this,
  // and disabled queries (no campaign selected) report false.
  if (
    bankAccounts.status === 'loading' ||
    processors.status === 'loading' ||
    settings.status === 'loading'
  ) {
    return 'loading';
  }
  if (settings.status === 'failed') return 'settings-failed';
  if (settings.status === 'offline') return 'settings-offline';
  // Without the accounts the gate can't tell "no sources yet" from a read that didn't answer.
  if (bankAccounts.status === 'failed') return 'bank-accounts-failed';
  if (bankAccounts.status === 'offline') return 'bank-accounts-offline';
  if (processors.status === 'failed') return 'processors-failed';
  if (processors.status === 'offline') return 'processors-offline';
  // A viewer without Finance Manage never reads the settings (idle): no history marker, no date.
  const settingsData = settings.status === 'ready' ? settings.data : null;

  const hasBank = (answered(bankAccounts)?.length ?? 0) > 0;
  const hasProcessor = processors.status === 'ready' && processors.data.length > 0;

  // A mid-cycle onboarded campaign (ADR-018) has real filed history — frozen externally-filed
  // reports + Reported ledger rows — before any live source is connected. Its
  // PriorFilingThroughDate is the backfill marker; when set, skip the "connect a source" gate so
  // the register shows that locked history (under the "Older than this period" divider) instead of
  // a blank empty state that reads as "nothing imported." Connecting a bank/processor stays the
  // obvious next step for the open period — it's just no longer a hard gate over real history.
  const hasPriorFilingHistory = settingsData?.priorFilingThroughDate != null;
  if (!hasBank && !hasProcessor && !hasPriorFilingHistory) return 'gate:no-sources';

  const formationDate = settingsData?.committeeFormationDate ?? null;
  if (!formationDate) return 'gate:no-formation-date';

  // The period decides no gate, so it never holds `loading`: the layout shows the ledger skeleton
  // while it loads and names a failed read. Holding `loading` here unmounted the spine on each
  // retry, and the remount refetched the period, so a failed read looped on the loading screen.
  // An unread period is not "none open".
  if (period.status === 'ready' && period.data === null) return 'no-period';
  return 'spine:active';
}
